import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import { colors } from "sexy";
import { useState, useEffect } from "react";
import { Project, ProjectPhoto, User } from "types";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { CustomImage } from "@components/ui/images/CustomImage";
import { showNotification } from "context/notification.service";
import PhotosMapModal from "@components/modals/PhotosMapModal";
import { useDataStore } from "@store/user_org";
import { isGuest as checkIsGuest } from "@utils/permissions";

const getVisiblePhotosFromProject = (project: Project, userData?: User) => {
  if (!project?.photos) return [];

  // If user is guest, filter out hidden photos
  // For non-guests, show all photos
  return project.photos.filter((photo) => !photo.hidden || userData);
};

interface PhotoModalProps {
  onClose: () => void;
  photo_id?: string;
  activeProject?: Project;
  setActiveProject?: (project: Project) => void;
  userData?: User;
}

export const PhotoModal = ({
  onClose,
  photo_id,
  activeProject,
  setActiveProject,
  userData,
}: PhotoModalProps) => {
  const storeUserData = useDataStore((state) => state.userData);
  const isGuest = checkIsGuest(userData || storeUserData);
  const [photo, setPhoto] = useState<ProjectPhoto | undefined>(
    activeProject?.photos.find((p) => p.id === photo_id),
  );
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [fetchedSize, setFetchedSize] = useState<number>(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!photo || photo.file.octets > 0) {
      setFetchedSize(0);
      return;
    }
    setFetchedSize(0);
    fetch(photo.file.url, { method: "HEAD" })
      .then((res) => {
        const len = res.headers.get("content-length");
        if (len) {
          const size = parseInt(len, 10);
          setFetchedSize(size);
          if (activeProject && setActiveProject) {
            setActiveProject({
              ...activeProject,
              photos: activeProject.photos.map((p) =>
                p.id === photo.id
                  ? { ...p, file: { ...p.file, octets: size } }
                  : p,
              ),
            });
          }
        }
      })
      .catch(() => {});
  }, [photo?.id]);

  function handleDelete() {
    if (!activeProject || !photo || !setActiveProject) return;

    setActiveProject({
      ...activeProject,
      photos: activeProject.photos.filter((p) => p.id !== photo.id),
    });
    onClose();
  }

  function handleToggleHidden() {
    if (!activeProject || !photo || !setActiveProject) return;

    const updatedPhoto = { ...photo, hidden: !photo.hidden };
    setPhoto(updatedPhoto);
    setActiveProject({
      ...activeProject,
      photos: activeProject.photos.map((p) =>
        p.id === photo.id ? updatedPhoto : p,
      ),
    });
  }

  function resetView() {
    setZoom(1);
    setRotation(0);
  }

  async function handleDownload() {
    if (!photo?.file?.url) return;
    try {
      const response = await fetch(photo.file.url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = photo.file.name || "photo.jpg";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      // Fallback: open in new tab if fetch fails (e.g. CORS)
      window.open(photo.file.url, "_blank");
    }
  }

  function goToNextPhoto() {
    if (!activeProject || !photo) return;

    const photos = getVisiblePhotosFromProject(activeProject, userData);
    const currentIndex = photos.findIndex((p) => p.id === photo.id);
    const nextIndex = (currentIndex + 1) % photos.length;
    const nextPhoto = photos[nextIndex];
    setPhoto(nextPhoto);
    resetView();
  }

  const isFacade =
    photo?.file.id === activeProject?.infos.general.photo_file?.id;

  function handleSetFacade() {
    if (!activeProject || !photo || !setActiveProject) return;

    setActiveProject({
      ...activeProject,
      infos: {
        ...activeProject.infos,
        general: {
          ...activeProject.infos.general,
          photo_file: photo.file,
        },
      },
    });
    showNotification({
      type: "success",
      message: "Photo de façade définie",
    });
  }

  function goToPreviousPhoto() {
    if (!activeProject || !photo) return;

    const photos = getVisiblePhotosFromProject(activeProject, userData);
    const currentIndex = photos.findIndex((p) => p.id === photo.id);
    const previousIndex = (currentIndex - 1 + photos.length) % photos.length;
    const previousPhoto = photos[previousIndex];
    setPhoto(previousPhoto);
    resetView();
  }

  useEffect(() => {
    if (photo_id && activeProject) {
      const foundPhoto = activeProject.photos.find((p) => p.id === photo_id);
      setPhoto(foundPhoto);
    }
  }, [photo_id, activeProject]);

  if (!photo || !activeProject) {
    return null;
  }

  return (
    <BaseModal
      width={isFullscreen ? "max-w-6xl" : "max-w-800"}
      display={isFullscreen ? "fullscreen" : undefined}
      title="Photo"
      startIcon={<Icon1 name="image" size={20} />}
      onClose={onClose}
    >
      {confirmationOpen && (
        <ConfirmationModal
          onClose={() => setConfirmationOpen(false)}
          onConfirm={handleDelete}
          title="Supprimer la photo"
          message="Êtes-vous sûr de vouloir supprimer cette photo ?"
        />
      )}
      {mapOpen && (
        <PhotosMapModal
          project={activeProject}
          currentPhotoId={photo.id}
          onClose={() => setMapOpen(false)}
          onSelectPhoto={(id) => {
            const found = activeProject.photos.find((p) => p.id === id);
            if (found) setPhoto(found);
            setMapOpen(false);
          }}
        />
      )}

      <div className="bg-white flex flex-col gap-4 p-4">
        {/* Photo Display */}
        <div
          className="relative w-full rounded-3xl overflow-hidden bg-neutral-100"
          style={{ height: isFullscreen ? "calc(100vh - 280px)" : "400px" }}
        >
          <img
            src={photo.file.url}
            alt="Photo"
            className="w-full h-full"
            style={{
              objectFit: "contain",
              transform: `scale(${zoom}) rotate(${rotation}deg)`,
              transition: "transform 0.2s ease",
            }}
            onWheel={(e) => {
              e.preventDefault();
              setZoom((z) => Math.min(5, Math.max(0.5, z + (e.deltaY < 0 ? 0.2 : -0.2))));
            }}
          />
          {photo.hidden && (
            <div className="absolute inset-0 bg-black/30 rounded-xl flex items-center justify-center">
              <Icon1 name="invisible" size={24} color="white" />
            </div>
          )}
        </div>

        {/* File size + name */}
        <p className="text-center text-sm text-neutral-400">
          {photo.file.name}
          {(() => {
            const size = photo.file.octets > 0 ? photo.file.octets : fetchedSize;
            if (size <= 0) return null;
            return <> — {size < 1024 * 1024
              ? `${(size / 1024).toFixed(0)} Ko`
              : `${(size / (1024 * 1024)).toFixed(1)} Mo`}</>;
          })()}
        </p>

        {/* Zoom / Rotate Controls */}
        <div className="flex flex-row justify-center gap-2 items-center">
          <button
            onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
            className="w-10 h-10 rounded-full bg-neutral-200 hover:bg-neutral-300 transition-colors flex items-center justify-center"
            title="Dézoomer"
          >
            <Icon1 name="minus" size={18} />
          </button>
          <span className="text-sm text-neutral-500 w-12 text-center">{Math.round(zoom * 100)}%</span>
          <button
            onClick={() => setZoom((z) => Math.min(5, z + 0.25))}
            className="w-10 h-10 rounded-full bg-neutral-200 hover:bg-neutral-300 transition-colors flex items-center justify-center"
            title="Zoomer"
          >
            <Icon1 name="plus" size={18} />
          </button>
          <button
            onClick={() => setRotation((r) => r + 90)}
            className="w-10 h-10 rounded-full bg-neutral-200 hover:bg-neutral-300 transition-colors flex items-center justify-center"
            title="Pivoter"
          >
            <Icon1 name="sync" size={18} />
          </button>
          {(zoom !== 1 || rotation !== 0) && (
            <button
              onClick={resetView}
              className="w-10 h-10 rounded-full bg-neutral-200 hover:bg-neutral-300 transition-colors flex items-center justify-center"
              title="Réinitialiser"
            >
              <Icon1 name="close" size={16} />
            </button>
          )}
        </div>

        {/* Navigation Controls */}
        <div className="flex flex-row justify-center gap-2.5 items-center">
          <button
            onClick={goToPreviousPhoto}
            className="w-12 h-12 rounded-full bg-neutral-800 hover:bg-neutral-700 transition-colors flex items-center justify-center"
            title="Photo précédente"
          >
            <Icon1 name="left" size={20} color="white" />
          </button>

          {!isGuest && (
            <button
              onClick={handleToggleHidden}
              className="w-12 h-12 rounded-full bg-neutral-800 hover:bg-neutral-700 transition-colors flex items-center justify-center"
              title={photo.hidden ? "Rendre visible" : "Masquer la photo"}
            >
              <Icon1 name="invisible" size={24} color="white" />
            </button>
          )}

          {!isGuest && (
            <button
              onClick={handleSetFacade}
              className={`w-12 h-12 rounded-full transition-colors flex items-center justify-center ${
                isFacade
                  ? "bg-blue-500 hover:bg-blue-400"
                  : "bg-neutral-800 hover:bg-neutral-700"
              }`}
              title="Définir comme photo de façade"
            >
              <Icon1 name="image" size={24} color="white" />
            </button>
          )}

          {!isGuest && (
            <button
              onClick={() => setMapOpen(true)}
              className="w-12 h-12 rounded-full bg-neutral-800 hover:bg-neutral-700 transition-colors flex items-center justify-center"
              title="Voir sur la carte"
            >
              <Icon1 name="position" size={24} color="white" />
            </button>
          )}

          {!isGuest && (
            <button
              onClick={() => setConfirmationOpen(true)}
              className="w-12 h-12 rounded-full bg-neutral-800 hover:bg-neutral-700 transition-colors flex items-center justify-center"
              title="Supprimer la photo"
            >
              <Icon1 name="delete" size={24} color="white" />
            </button>
          )}

          <button
            onClick={goToNextPhoto}
            className="w-12 h-12 rounded-full bg-neutral-800 hover:bg-neutral-700 transition-colors flex items-center justify-center"
            title="Photo suivante"
          >
            <Icon1 name="right" size={20} color="white" />
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t-1 p-4 flex flex-row justify-between items-center">
        <div className="flex flex-row gap-2">
          <button
            onClick={handleDownload}
            className="h-10 px-4 rounded-xl bg-neutral-200 hover:bg-neutral-300 transition-colors flex items-center justify-center gap-2"
            title="Télécharger"
          >
            <Icon1 name="download" size={18} />
            <span className="text-sm">Télécharger</span>
          </button>
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="h-10 px-4 rounded-xl bg-neutral-200 hover:bg-neutral-300 transition-colors flex items-center justify-center gap-2"
            title={isFullscreen ? "Réduire" : "Plein écran"}
          >
            <Icon1 name="full-screen" size={18} />
            <span className="text-sm">{isFullscreen ? "Réduire" : "Plein écran"}</span>
          </button>
        </div>
        <Button variant="small" label="Fermer" onClick={onClose} />
      </div>
    </BaseModal>
  );
};
